import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import adminAxios from '../../services/adminAxios';
import '../../assets/css/admin/user-details.css';

const UserDetailsPage = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      
      const response = await adminAxios.get(`/api/admin/users/${userId}`);
      
      if (response.data.success) {
        setUser(response.data.user);
        setError(null);
      } else {
        setError('Failed to load user details');
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Error loading user details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format date properly
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading-container">
          <div className="spinner"></div>
          <p>Loading user details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="alert alert-danger">{error}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="user-details-page">
        {/* Back button */}
        <div className="back-link">
          <Link to="/admin/users">
            <i className="fa fa-arrow-left"></i> Back to Users
          </Link>
        </div>
        
        {/* User Header Card */}
        <div className="user-details-header">
          <div className="user-header-content">
            <div className="user-avatar">
              {user?.profile_image ? (
                <img src={user.profile_image} alt={user.name} />
              ) : (
                <i className="fa fa-user-circle"></i>
              )}
            </div>
            
            <div className="user-header-info">
              <h1 className="user-title">{user?.name || 'User'}</h1>
              
              <div className="user-meta-info">
                <div className="user-meta-item">
                  <i className="fa fa-envelope"></i>
                  <span>{user?.email || 'No email provided'}</span>
                </div>
                
                <div className="user-meta-item">
                  <i className="fa fa-map-marker"></i>
                  <span>{user?.location || 'No location specified'}</span>
                </div>
                
                <div className="user-meta-item">
                  <i className="fa fa-calendar"></i>
                  <span>Joined {formatDate(user?.created_at)}</span>
                </div>
              </div>
              
              <div className="user-type-badge">
                {user?.user_type === 'event_promoter' ? 'Event Promoter' : 
                 user?.user_type === 'event_attendee' ? 'Event Attendee' : 'User'}
              </div>
            </div>
          </div>
          
          <div className="user-header-actions">
            <button className="btn btn-outline-primary">
              <i className="fa fa-pencil"></i> Edit User
            </button>
            <button className="btn btn-outline-danger">
              <i className="fa fa-ban"></i> Suspend
            </button>
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="modern-tabs">
          <div 
            className={`tab-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => handleTabClick('profile')}
          >
            <i className="fa fa-user"></i> Profile
          </div>
          
          {user?.user_type === 'event_promoter' && (
            <div 
              className={`tab-item ${activeTab === 'events' ? 'active' : ''}`}
              onClick={() => handleTabClick('events')}
            >
              <i className="fa fa-calendar"></i> Events 
              <span className="tab-count">{user.events?.length || 0}</span>
            </div>
          )}
          
          {user?.user_type === 'event_attendee' && (
            <div 
              className={`tab-item ${activeTab === 'checkins' ? 'active' : ''}`}
              onClick={() => handleTabClick('checkins')}
            >
              <i className="fa fa-check-square-o"></i> Check-ins
              <span className="tab-count">{user.checkins?.length || 0}</span>
            </div>
          )}
          
          <div 
            className={`tab-item ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => handleTabClick('activity')}
          >
            <i className="fa fa-line-chart"></i> Activity
          </div>
        </div>
        
        {/* Tab Content */}
        <div className="tab-content">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="tab-pane">
              <div className="details-grid">
                <div className="details-main-card">
                  <div className="card-title">
                    <i className="fa fa-id-card"></i> User Information
                  </div>
                  <div className="card-content">
                    <div className="info-row">
                      <div className="info-label">Full Name</div>
                      <div className="info-value">{user?.name || 'Not provided'}</div>
                    </div>
                    
                    <div className="info-row">
                      <div className="info-label">Email Address</div>
                      <div className="info-value">{user?.email || 'Not provided'}</div>
                    </div>
                    
                    <div className="info-row">
                      <div className="info-label">User Type</div>
                      <div className="info-value">
                        {user?.user_type === 'event_promoter' ? 'Event Promoter' : 
                         user?.user_type === 'event_attendee' ? 'Event Attendee' : 'Regular User'}
                      </div>
                    </div>
                    
                    <div className="info-row">
                      <div className="info-label">Location</div>
                      <div className="info-value">{user?.location || 'Not provided'}</div>
                    </div>
                    
                    <div className="info-row">
                      <div className="info-label">Joined On</div>
                      <div className="info-value">{formatDate(user?.created_at)}</div>
                    </div>
                  </div>
                </div>
                
                {/* Business Details for Event Promoters */}
                {user?.user_type === 'event_promoter' && user?.business && (
                  <div className="details-sidebar-card">
                    <div className="card-title">
                      <i className="fa fa-briefcase"></i> Business Details
                    </div>
                    <div className="card-content">
                      <div className="info-row">
                        <div className="info-label">Business Name</div>
                        <div className="info-value">{user.business.business_name || 'Not provided'}</div>
                      </div>
                      
                      <div className="info-row">
                        <div className="info-label">Business Type</div>
                        <div className="info-value">{user.business.business_type || 'Not specified'}</div>
                      </div>
                      
                      <div className="info-row">
                        <div className="info-label">Business Location</div>
                        <div className="info-value">{user.business.business_location || 'Not specified'}</div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Stats Card */}
                <div className={`details-stats-card ${user?.user_type === 'event_promoter' && user?.business ? 'full-width' : ''}`}>
                  <div className="card-title">
                    <i className="fa fa-chart-bar"></i> Statistics
                  </div>
                  <div className="card-content">
                    <div className="stats-grid">
                      {user?.user_type === 'event_promoter' ? (
                        <>
                          <div className="stat-box">
                            <div className="stat-number">{user.events?.length || 0}</div>
                            <div className="stat-label">Total Events</div>
                          </div>
                          <div className="stat-box">
                            <div className="stat-number">{user.events?.filter(e => new Date(e.event_date) > new Date()).length || 0}</div>
                            <div className="stat-label">Upcoming Events</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="stat-box">
                            <div className="stat-number">{user.checkins?.length || 0}</div>
                            <div className="stat-label">Total Check-ins</div>
                          </div>
                          <div className="stat-box">
                            <div className="stat-number">0</div>
                            <div className="stat-label">Memories</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Events Tab for Promoters */}
          {activeTab === 'events' && user?.user_type === 'event_promoter' && (
            <div className="tab-pane">
              {user.events && user.events.length > 0 ? (
                <div className="events-grid">
                  {user.events.map(event => (
                    <div key={event.id} className="event-card">
                      <div className="event-card-header">
                        <span className={`status-indicator ${event.status || 'active'}`}></span>
                        <span className={`status-badge ${event.status || 'active'}`}>
                          {event.status || 'Active'}
                        </span>
                      </div>
                      <div className="event-card-body">
                        <h3 className="event-title">{event.event_title}</h3>
                        <div className="event-meta">
                          <div className="event-meta-item">
                            <i className="fa fa-calendar"></i>
                            <span>{formatDate(event.event_date)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="event-card-footer">
                        <Link to={`/admin/events/${event.id}`} className="btn btn-sm btn-outline-primary">
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">
                    <i className="fa fa-calendar"></i>
                  </div>
                  <h3>No Events Found</h3>
                  <p>This user hasn't created any events yet.</p>
                </div>
              )}
            </div>
          )}
          
          {/* Check-ins Tab for Attendees */}
          {activeTab === 'checkins' && user?.user_type === 'event_attendee' && (
            <div className="tab-pane">
              {user.checkins && user.checkins.length > 0 ? (
                <div className="checkins-list">
                  {user.checkins.map(checkin => (
                    <div key={checkin.id} className="checkin-card">
                      <div className="checkin-event">
                        <h3>{checkin.event_title}</h3>
                        <span className="checkin-date">
                          <i className="fa fa-clock-o"></i> {new Date(checkin.checkin_date).toLocaleString()}
                        </span>
                      </div>
                      <div className="checkin-footer">
                        <span className={`visibility-badge ${checkin.visibility || 'public'}`}>
                          {checkin.visibility || 'Public'}
                        </span>
                        <Link to={`/admin/events/${checkin.event_id}`} className="btn btn-sm btn-outline-primary">
                          View Event
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">
                    <i className="fa fa-check-square-o"></i>
                  </div>
                  <h3>No Check-ins Found</h3>
                  <p>This user hasn't checked in to any events yet.</p>
                </div>
              )}
            </div>
          )}
          
          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div className="tab-pane">
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fa fa-line-chart"></i>
                </div>
                <h3>Activity Log</h3>
                <p>User activity tracking will be available soon.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserDetailsPage;